import { MenuController } from "./components/menu/menu.controller";
import { UnsupportedFeatureController } from "./components/unsupported-feature-screen/unsupported-feature-screen.controller";
import {
  MissingStageError,
  NoRemainingStagesError,
} from "./generation/multi_stage.generator";
import {
  MissingManifestError,
  StimuliController,
} from "./generation/stimuli.bank";
import { ExperimentState } from "./index.models";
import { ExperimentTestRecord } from "./interaction_tracking/experiment_event";
import { NoopDriver, SocialMediaDriver } from "./interaction_tracking/network";
import { UserInteractionTracker } from "./interaction_tracking/user_interaction_tracker";
import { ExperimentManifest } from "./manifest/models/manifest.models";
import { PostData } from "./posts/post.models";
import { ReactiveController } from "./utils/controller";
import { ScreenSizeListener, Size } from "./utils/screen-size-listener";
import { computePostSizes } from "./utils/screen_resizer";

/**
 * The main application controller that manages all top level events and business logic for the experiment.
 * Primarily, this is responsible for loading and parsing the experiment manifest into posts to be displayed.
 * It also handles user interaction tracking with the page.
 */
export class ExperimentController extends ReactiveController<ExperimentState> {
  private screenSizeListener: ScreenSizeListener = new ScreenSizeListener();

  private debug: boolean = false;
  private subjectId?: string;
  private condition?: string;
  private disableMultistage: boolean = false;

  private serverDriver?: SocialMediaDriver;
  private interactionTracker?: UserInteractionTracker;
  private testId?: string;

  private manifest?: ExperimentManifest;
  private stage?: number;

  constructor() {
    super({
      posts: [],
      loading: true,
      menuController: new MenuController({ currentPage: "home" }),
      unsupportedFeatureController: new UnsupportedFeatureController(),
      mobileSupported: true,
      sponsored: [],
      birthdays: [],
      contacts: [],
      onlineUsers: [],
      showContacts: true,
    });
  }

  async initialize() {
    await super.initialize();

    const paramsValid = this.parseQueryParams();
    if (!paramsValid) return;

    if (this.debug) {
      this.serverDriver = new NoopDriver();
    } else {
      this.serverDriver = new SocialMediaDriver();
    }

    const manifestExists = await this.fetchManifest();
    if (!manifestExists) return;

    try {
      await this.parseExperimentData();
    } catch (error) {
      console.error("FATAL: Parsing experiment manifest failed!", error);
      this.onManifestMissing(error);
      return;
    }

    this.screenSizeListener.initialize();
    this.screenSizeListener.stream.subscribe(this.onScreenSizeChanged);

    this.interactionTracker = new UserInteractionTracker(
      this.serverDriver,
      false,
      this.state.posts,
    );

    await this.uploadTestRecord();
  }

  async uploadTestRecord() {
    const strippedPosts: PostData[] = [];

    for (const post of this.state.posts) {
      const strippedPost = { ...post };
      strippedPost.userFollowup = undefined;
      strippedPosts.push(strippedPost);
    }

    const experimentTestRecord = new ExperimentTestRecord(
      // @ts-ignore
      Date.now(),
      this.subjectId,
      null,
      strippedPosts,
      this.stage,
    );
    const data =
      await this.serverDriver!.putExperimentTest(experimentTestRecord);
    // Reload the experiment test record from json.
    experimentTestRecord.fromJSON(data[0]);
    this.testId = experimentTestRecord.id;
    this.checkExperimentIsLoaded();
  }

  async dispose() {
    await super.dispose();
    this.interactionTracker?.stopTracking();
  }

  /**
   * Parse experiment related params from the browser url.
   * Will set dataLoadError if required parameters are not provided or are invalid.
   * @returns false if all required params are not provided.
   */
  parseQueryParams() {
    const params = new URLSearchParams(window.location.search);

    this.debug = params.has("debug") ? params.get("debug") === "true" : false;
    this.subjectId = params.get("b") ?? undefined;
    this.condition = params.get("a") ?? undefined;
    this.disableMultistage = params.has("noMulti")
      ? params.get("noMulti") === "true"
      : false;

    if (
      this.condition == null ||
      (this.condition != "1" && this.condition != "2")
    ) {
      this.onConditionMissing();
      return false;
    } else if (this.subjectId == null) {
      this.onSubjectIdMissing();
      return false;
    }

    return true;
  }

  onConditionMissing() {
    this.setState({
      error: "no-condition",
    });
  }

  onSubjectIdMissing() {
    this.setState({
      error: "no-subject-id",
    });
  }

  onManifestMissing(exception?: any) {
    this.setState({
      error: "no-manifest",
      exception: exception,
    });
  }

  /**
   * Update our state with the latest screen size data from our listener.
   * @param size The new screen size.
   */
  onScreenSizeChanged(size?: Size | undefined) {
    if (size == null) return;

    this.setState({
      posts: computePostSizes(this.state.posts, size.height),
      screenWidth: size.width,
      screenHeight: size.height,
    });
    this.checkExperimentIsLoaded();
  }

  async fetchManifest() {
    const response = await fetch("./experiment_manifest.json");
    if (!response.ok) {
      this.onManifestMissing();
      return false;
    }

    this.manifest = await response.json();
    this.setState({
      contact: this.manifest?.contact,
      name: this.manifest?.name,
      instructions: this.manifest?.instructions,
      version: this.manifest?.version,
      mobileSupported: this.manifest?.supports_mobile ?? true,
    });
    return true;
  }

  async parseExperimentData() {
    const stimuliController = new StimuliController(this.serverDriver!);

    try {
      await stimuliController.load(
        this.subjectId!,
        this.condition!,
        this.disableMultistage,
      );
    } catch (error) {
      if (error instanceof MissingManifestError) {
        this.setState({
          error: "no-manifest",
          exception: error,
        });
      } else if (
        error instanceof MissingStageError ||
        error instanceof NoRemainingStagesError
      ) {
        this.onConditionMissing();
        return;
      } else {
        throw error;
      }
    }

    this.stage = stimuliController.stage;
    let posts = stimuliController.posts.slice();

    if (this.state.screenHeight != null) {
      posts = computePostSizes(posts, this.state.screenHeight);
    }

    this.setState({
      posts: posts,
      branding: stimuliController.stimuli.branding,
      sponsored: [stimuliController.sidebar.sponsored],
      birthdays: stimuliController.sidebar.birthdays,
      contacts: stimuliController.sidebar.contacts,
      shortcuts: stimuliController.sidebar.shortcuts,
      onlineUsers: stimuliController.sidebar.onlineUsers,
      showContacts: stimuliController.sidebar.showContacts,
      leaderboard: stimuliController.sidebar.leaderboard,
      user: stimuliController.stimuli.user,
    });
    this.checkExperimentIsLoaded();
  }

  getNetworkDriver(): SocialMediaDriver {
    return this.serverDriver!;
  }

  getTestId(): string {
    if (this.testId == null)
      throw new Error(
        "Cannot retrieve test id for experiment as it hasn't been established yet!",
      );
    return this.testId;
  }

  startInteractionTracking() {
    if (!this.interactionTracker) return;
    this.interactionTracker.startTracking(
      this.subjectId,
      this.stage,
      this.testId,
    );
  }

  checkExperimentIsLoaded() {
    if (
      this.state.screenHeight != null &&
      this.state.screenWidth != null &&
      this.state.posts.length > 0 &&
      this.testId != null
    ) {
      this.setState({
        loading: false,
      });
      this.startInteractionTracking();
    }
  }
}
